import React, { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { eggState, announceEgg, playChime, playHowl, moonPhase, isFullMoon, meteorShowerTonight } from '../../lib/eggs';

const makeWish = (point) => {
  eggState.burst = { x: point.x, y: point.y, z: point.z, at: performance.now() }; // gold sparkles (EasterEggs.jsx)
  playChime();
  announceEgg('wish', 'You caught a shooting star. Your wish is safe with the gallery.', '🌠');
};

// Soft round glow texture drawn on a canvas (no image download needed).
const makeGlowTexture = (inner, outer) => {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, inner);
  g.addColorStop(0.25, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

// Note: sky objects are drawn as *opaque* (transparent: false) on purpose. The glass roof uses
// transmission, which only shows opaque objects behind it. renderOrder puts them after the sky dome.
const SKY_RENDER_ORDER = 1;

// Moon with craters (procedural canvas texture) and a halo. Fades with night; dims behind clouds.
// It shows tonight's real phase: each texel of the sphere is lit or in shadow depending on where
// the sun is (worked out from the phase), with a faint earthshine on the dark side.
const MOON_POSITION = [-110, 120, -260];
const MOON_ROTATION = [0.3, 0.6, 0];

const makeMoonTexture = (phase) => {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#f4f1e4';
  ctx.fillRect(0, 0, size, size);
  // Deterministic "random" so the moon looks the same every visit.
  let seed = 7;
  const rand = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  for (let i = 0; i < 70; i++) {
    const x = rand() * size, y = rand() * size, r = 6 + rand() * (i < 8 ? 70 : 22);
    ctx.fillStyle = `rgba(150, 145, 130, ${0.12 + rand() * 0.22})`;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  // Light and shadow for the phase. V points from the moon to us; the sun direction swings from
  // behind the moon (new) round to behind us (full).
  const V = new THREE.Vector3(...MOON_POSITION).negate().normalize();
  const side = new THREE.Vector3(0, 1, 0).cross(V).normalize();
  const e = phase * Math.PI * 2;
  const S = V.clone().multiplyScalar(-Math.cos(e)).addScaledVector(side, Math.sin(e)).normalize();
  const rot = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(...MOON_ROTATION));
  const img = ctx.getImageData(0, 0, size, size), d = img.data, n = new THREE.Vector3();
  for (let y = 0; y < size; y++) {
    const theta = ((y + 0.5) / size) * Math.PI; // three's sphere: v = 1 at the top (canvas row 0)
    for (let x = 0; x < size; x++) {
      const phi = ((x + 0.5) / size) * Math.PI * 2;
      n.set(-Math.cos(phi) * Math.sin(theta), Math.cos(theta), Math.sin(phi) * Math.sin(theta)).applyMatrix4(rot);
      const lit = THREE.MathUtils.smoothstep(n.dot(S), -0.04, 0.06);
      const k = 0.07 + 0.93 * lit; // earthshine
      const i = (y * size + x) * 4;
      d[i] *= k; d[i + 1] *= k; d[i + 2] *= k * (lit > 0.5 ? 1 : 1.12);
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

// Secret: on full-moon nights a wolf howls far away, now and then.
const WolfHowl = ({ nightRef, weatherRef }) => {
  const st = useRef({ next: 20, told: false });
  const full = useMemo(() => isFullMoon(), []);
  useFrame(({ clock }) => {
    if (!full) return;
    const t = clock.elapsedTime, s = st.current;
    const clear = nightRef.current * (1 - (weatherRef ? weatherRef.current.cloud : 0) * 0.7);
    if (clear < 0.8) { s.next = Math.max(s.next, t + 8); return; }
    if (t < s.next) return;
    s.next = t + 50 + Math.random() * 40;
    playHowl();
    if (!s.told) {
      s.told = true;
      setTimeout(() => announceEgg('fullmoon', 'A full moon tonight… and somewhere far off, a wolf is howling.', '🐺'), 1500);
    }
  });
  return null;
};

export const Moon = ({ nightRef, weatherRef }) => {
  const groupRef = useRef();
  const moonMat = useRef();
  const haloMat = useRef();
  const phase = useMemo(() => moonPhase(), []);
  const moonTex = useMemo(() => makeMoonTexture(phase), [phase]);
  // a fuller moon has a brighter halo
  const haloStrength = 0.08 + 0.22 * (1 - Math.abs(phase - 0.5) * 2);
  const haloTex = useMemo(() => makeGlowTexture('rgba(255,250,225,0.9)', 'rgba(255,250,225,0)'), []);

  useFrame(() => {
    const n = nightRef.current;
    const cover = weatherRef ? weatherRef.current.cloud : 0; // clouds hide the moon
    const visibility = n * (1 - cover * 0.85);
    // Hide by scaling to ~0 rather than toggling visibility, so nothing needs compiling mid-visit.
    if (groupRef.current) groupRef.current.scale.setScalar(visibility > 0.01 ? 1 : 0.0001);
    if (moonMat.current) moonMat.current.color.setScalar(visibility); // fade by brightness (opaque)
    if (haloMat.current) haloMat.current.opacity = visibility * haloStrength;
  });

  return (
    <group ref={groupRef} position={MOON_POSITION} scale={0.0001}>
      <WolfHowl nightRef={nightRef} weatherRef={weatherRef} />
      <mesh rotation={MOON_ROTATION} renderOrder={SKY_RENDER_ORDER} frustumCulled={false}>
        <sphereGeometry args={[14, 48, 48]} />
        <meshBasicMaterial ref={moonMat} map={moonTex} color="#000" fog={false} toneMapped={false} />
      </mesh>
      <sprite scale={[75, 75, 1]} renderOrder={SKY_RENDER_ORDER + 1} frustumCulled={false}>
        <spriteMaterial ref={haloMat} map={haloTex} transparent={false} opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} fog={false} />
      </sprite>
    </group>
  );
};

// Twinkling stars in three size classes and slightly different colours.
// Sizes are multiplied by the screen's pixel ratio so they look right on phones too.
const starVertex = /* glsl */ `
  attribute float aSize;
  attribute float aPhase;
  attribute float aSpeed;
  attribute vec3 aColor;
  uniform float uTime;
  uniform float uPixelRatio;
  varying float vTwinkle;
  varying vec3 vColor;
  void main() {
    vTwinkle = 0.35 + 0.65 * pow(0.5 + 0.5 * sin(uTime * aSpeed + aPhase), 3.0);
    vColor = aColor;
    gl_PointSize = aSize * uPixelRatio * (0.75 + 0.5 * vTwinkle);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const starFragment = /* glsl */ `
  uniform float uOpacity;
  varying float vTwinkle;
  varying vec3 vColor;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    float core = smoothstep(0.5, 0.0, d);
    // Faint cross sparkle, visible on the bigger stars.
    float sparkle = max(0.0, 1.0 - abs(c.x) * 18.0) * smoothstep(0.5, 0.0, abs(c.y))
                  + max(0.0, 1.0 - abs(c.y) * 18.0) * smoothstep(0.5, 0.0, abs(c.x));
    float a = (core * core + sparkle * 0.35) * vTwinkle * uOpacity;
    gl_FragColor = vec4(vColor * a, a); // additive
  }
`;

const STAR_COLORS = ['#ffffff', '#ffffff', '#fff4e0', '#e6eeff', '#cfe0ff', '#ffe2c4'].map((c) => new THREE.Color(c));

export const TwinklingStars = ({ nightRef, weatherRef, count = 2500 }) => {
  const pointsRef = useRef();
  const { gl } = useThree();
  const { geometry, material } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const size = new Float32Array(count);
    const phase = new Float32Array(count);
    const speed = new Float32Array(count);
    const color = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const y = 0.06 + Math.random() * 0.94;
      const r = Math.sqrt(1 - y * y);
      const R = 380;
      pos[i * 3] = Math.cos(theta) * r * R;
      pos[i * 3 + 1] = y * R;
      pos[i * 3 + 2] = Math.sin(theta) * r * R;
      const roll = Math.random();
      // 75% tiny, 20% medium, 5% big and bright
      size[i] = roll < 0.75 ? 1.2 + Math.random() * 1.3 : roll < 0.95 ? 3 + Math.random() * 2.5 : 7 + Math.random() * 6;
      phase[i] = Math.random() * Math.PI * 2;
      speed[i] = 0.6 + Math.random() * 3;
      const c = STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)];
      color.set([c.r, c.g, c.b], i * 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1));
    geo.setAttribute('aSpeed', new THREE.BufferAttribute(speed, 1));
    geo.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 }, uPixelRatio: { value: 1 } },
      vertexShader: starVertex,
      fragmentShader: starFragment,
      transparent: false, // opaque so the glass roof shows it (see note above)
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    return { geometry: geo, material: mat };
  }, [count]);

  useFrame((state) => {
    const n = nightRef.current;
    const cover = weatherRef ? weatherRef.current.cloud : 0;
    const opacity = n * (1 - cover * 0.95);
    material.uniforms.uTime.value = state.clock.elapsedTime;
    material.uniforms.uOpacity.value = opacity;
    material.uniforms.uPixelRatio.value = Math.min(gl.getPixelRatio(), 2);
    geometry.setDrawRange(0, opacity > 0.01 ? count : 0); // draw nothing by day
    if (pointsRef.current) pointsRef.current.rotation.y += 0.00004;
  });

  return <points ref={pointsRef} geometry={geometry} material={material} frustumCulled={false} renderOrder={SKY_RENDER_ORDER} />;
};

// Shooting stars: a bright streak crosses part of the sky every few seconds on clear nights.
const makeStreakTexture = () => {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 16;
  const ctx = c.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 256, 0);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.85, 'rgba(220,235,255,0.7)');
  g.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 16);
  const v = ctx.createLinearGradient(0, 0, 0, 16); // soften the edges
  v.addColorStop(0, 'rgba(0,0,0,1)'); v.addColorStop(0.5, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,1)');
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, 256, 16);
  return new THREE.CanvasTexture(c);
};

const ShootingStar = ({ nightRef, weatherRef, texture, delay, gap = [3, 10], onFirst }) => {
  const meshRef = useRef();
  const matRef = useRef();
  const state = useRef({ active: false, t: 0, next: delay, start: new THREE.Vector3(), dir: new THREE.Vector3(), duration: 1 });
  const basis = useMemo(() => ({ x: new THREE.Vector3(), y: new THREE.Vector3(), z: new THREE.Vector3(), m: new THREE.Matrix4() }), []);

  useFrame(({ clock }, delta) => {
    const s = state.current;
    const mesh = meshRef.current;
    if (!mesh) return;
    const now = clock.elapsedTime;
    const clearNight = nightRef.current * (1 - (weatherRef ? weatherRef.current.cloud : 0));

    if (!s.active) {
      mesh.scale.setScalar(0.0001);
      if (now > s.next && clearNight > 0.6) {
        // Start somewhere high in the sky, travelling sideways and slightly down.
        const az = Math.random() * Math.PI * 2;
        const el = 0.5 + Math.random() * 0.6;
        s.start.set(Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el)).multiplyScalar(340);
        const tangent = new THREE.Vector3(-Math.sin(az), 0, Math.cos(az)).multiplyScalar(Math.random() < 0.5 ? 1 : -1);
        s.dir.copy(tangent).addScaledVector(new THREE.Vector3(0, -1, 0), 0.35 + Math.random() * 0.3).normalize();
        s.duration = 0.7 + Math.random() * 0.6;
        s.t = 0;
        s.active = true;
        onFirst?.();
      }
      return;
    }

    s.t += delta;
    const k = s.t / s.duration;
    if (k >= 1) {
      s.active = false;
      s.next = now + gap[0] + Math.random() * (gap[1] - gap[0]);
      return;
    }
    const pos = s.start.clone().addScaledVector(s.dir, k * 160);
    // Orient the streak along its path and facing the hall.
    basis.z.copy(pos).negate().normalize();
    basis.x.copy(s.dir).addScaledVector(basis.z, -s.dir.dot(basis.z)).normalize();
    basis.y.crossVectors(basis.z, basis.x);
    basis.m.makeBasis(basis.x, basis.y, basis.z);
    mesh.quaternion.setFromRotationMatrix(basis.m);
    mesh.position.copy(pos);
    mesh.scale.setScalar(1);
    if (matRef.current) matRef.current.opacity = Math.sin(Math.PI * k) * clearNight;
  });

  return (
    <mesh ref={meshRef} scale={0.0001} renderOrder={SKY_RENDER_ORDER} frustumCulled={false}>
      {/* Secret: click a shooting star to make a wish (a wider, invisible target makes it catchable) */}
      <mesh visible={false} onClick={(e) => { e.stopPropagation(); makeWish(e.point); }}>
        <planeGeometry args={[60, 16]} />
      </mesh>
      <planeGeometry args={[45, 1.1]} />
      <meshBasicMaterial
        ref={matRef}
        map={texture}
        transparent={false} // opaque + additive so the glass roof shows it
        opacity={0}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        side={THREE.DoubleSide}
        fog={false}
        toneMapped={false}
      />
    </mesh>
  );
};

// On the nights of real meteor showers (and Tanabata, the star festival) there are many more,
// coming every second or two. The extra streaks always exist and share the same material, so a
// meteor-shower night compiles nothing new; they simply never start on ordinary nights.
export const ShootingStars = ({ nightRef, weatherRef, meteors = false }) => {
  const texture = useMemo(makeStreakTexture, []);
  const shower = useMemo(() => meteorShowerTonight() || (meteors ? 'Tanabata' : null), [meteors]);
  const told = useRef(false);
  const tell = () => {
    if (!shower || told.current) return;
    told.current = true;
    announceEgg('meteors', shower === 'Tanabata'
      ? 'The stars are out for Tanabata. Look up: a shower of shooting stars!'
      : `It's the night of the ${shower} meteor shower. Look up: shooting stars everywhere!`, '☄️');
  };
  return (
    <>
      <ShootingStar nightRef={nightRef} weatherRef={weatherRef} texture={texture} delay={2} gap={shower ? [0.5, 2] : [3, 10]} onFirst={tell} />
      <ShootingStar nightRef={nightRef} weatherRef={weatherRef} texture={texture} delay={6} gap={shower ? [0.5, 2] : [3, 10]} />
      {[0.8, 1.6, 2.4, 3.2].map((d) => (
        <ShootingStar key={d} nightRef={nightRef} weatherRef={weatherRef} texture={texture} delay={shower ? d : Infinity} gap={shower ? [0.6, 2.5] : [Infinity, Infinity]} />
      ))}
    </>
  );
};

// ---------------------------------------------------------------- aurora borealis
// Glowing curtains low in the northern sky on dry nights. Each curtain is a long ribbon (a few
// hundred triangles) bent into folds that drift slowly; the shader draws the bright green lower
// edge, fine vertical rays, and the faint violet glow higher up. Clouds dim it, rain hides it.
// Drawn opaque + additive like the stars (so it shows through the glass roof), and switched off
// with drawRange by day, so its shader is compiled up front and nothing compiles mid-visit.
const auroraVertex = /* glsl */ `
  attribute float aU;       // 0..1 along the curtain
  attribute float aSeed;
  uniform float uTime;
  varying vec2 vUv;
  varying float vSeed;
  void main() {
    vUv = vec2(aU, uv.y);
    vSeed = aSeed;
    vec3 p = position;
    // slow folds travelling along the curtain
    float fold = sin(aU * 9.0 + uTime * 0.07 + aSeed * 3.0) * 0.6 + sin(aU * 23.0 - uTime * 0.11 + aSeed) * 0.4;
    vec2 dir = normalize(p.xz);
    p.xz += dir * fold * 22.0;
    p.y += sin(aU * 5.0 + uTime * 0.05 + aSeed * 2.0) * 14.0 * (1.0 - uv.y * 0.3);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;
const auroraFragment = /* glsl */ `
  uniform float uTime;
  uniform float uStrength;
  varying vec2 vUv;
  varying float vSeed;
  float hash(float n) { return fract(sin(n) * 43758.5453); }
  float noise1(float x) { float i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f); return mix(hash(i), hash(i + 1.0), f); }
  void main() {
    float u = vUv.x, v = vUv.y;
    // fine vertical rays that shimmer
    float rays = noise1(u * 260.0 + vSeed * 17.0 + uTime * 0.35) * 0.6 + noise1(u * 90.0 - uTime * 0.2 + vSeed) * 0.4;
    rays = 0.35 + 0.65 * pow(rays, 1.6);
    // brighter and dimmer stretches drifting along the curtain
    float patches = smoothstep(0.25, 0.85, noise1(u * 7.0 - uTime * 0.04 + vSeed * 5.0));
    float ends = sin(3.14159 * u);
    // sharp glowing lower edge, fading upwards
    float edge = smoothstep(0.0, 0.05, v) * exp(-v * 3.2);
    float glowTop = smoothstep(0.15, 0.6, v) * (1.0 - smoothstep(0.6, 1.0, v)) * 0.35;
    vec3 green = vec3(0.12, 1.0, 0.45);
    vec3 teal = vec3(0.1, 0.75, 0.7);
    vec3 violet = vec3(0.55, 0.2, 0.85);
    vec3 col = mix(green, teal, smoothstep(0.1, 0.35, v));
    col = mix(col, violet, smoothstep(0.3, 0.85, v));
    float k = (edge * rays + glowTop * (0.5 + 0.5 * rays)) * patches * ends * uStrength;
    gl_FragColor = vec4(col * k * 0.9, 1.0);
  }
`;

export const Aurora = ({ nightRef, weatherRef }) => {
  const { geometry, material } = useMemo(() => {
    // three curtains across the northern sky (north = −z), at different distances and heights
    const curtains = [
      { a0: -2.55, a1: -0.65, R: 400, base: 120, height: 210, seed: 0.3 },
      { a0: -2.2, a1: -1.0, R: 360, base: 150, height: 170, seed: 1.7 },
      { a0: -2.85, a1: -1.6, R: 430, base: 105, height: 150, seed: 2.9 },
    ];
    const SEG = 140;
    const pos = [], uv = [], au = [], seed = [], idx = [];
    curtains.forEach((c) => {
      const start = pos.length / 3;
      for (let i = 0; i <= SEG; i++) {
        const f = i / SEG, a = c.a0 + (c.a1 - c.a0) * f;
        const x = Math.cos(a) * c.R, z = Math.sin(a) * c.R;
        for (let j = 0; j < 2; j++) {
          pos.push(x, c.base + j * c.height, z);
          uv.push(f, j); au.push(f); seed.push(c.seed);
        }
      }
      for (let i = 0; i < SEG; i++) {
        const k = start + i * 2;
        idx.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
      }
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setAttribute('aU', new THREE.Float32BufferAttribute(au, 1));
    geo.setAttribute('aSeed', new THREE.Float32BufferAttribute(seed, 1));
    geo.setIndex(idx);
    geo.setDrawRange(0, 0);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uStrength: { value: 0 } },
      vertexShader: auroraVertex,
      fragmentShader: auroraFragment,
      transparent: false, // opaque + additive: visible through the glass roof (see note above)
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    return { geometry: geo, material: mat };
  }, []);
  const level = useRef(0);

  useFrame((state, delta) => {
    const n = nightRef.current;
    const w = weatherRef ? weatherRef.current : { rain: 0, storm: 0, cloud: 0, fog: 0 };
    // only on dry nights; thick cloud or fog veils it
    // only in full night (not the evening twilight)
    const target = THREE.MathUtils.smoothstep(n, 0.7, 1) * (1 - Math.max(w.rain, w.storm)) * (1 - 0.8 * w.cloud) * (1 - 0.7 * w.fog);
    level.current += (target - level.current) * Math.min(1, delta * 0.4);
    // it breathes: slowly brightens and fades over a minute or so
    const t = state.clock.elapsedTime;
    const pulse = 0.65 + 0.35 * Math.sin(t * 0.09) * Math.sin(t * 0.037 + 1.3);
    material.uniforms.uTime.value = t;
    material.uniforms.uStrength.value = level.current * pulse;
    geometry.setDrawRange(0, level.current > 0.01 ? geometry.index.count : 0);
  });

  return <mesh geometry={geometry} material={material} frustumCulled={false} renderOrder={SKY_RENDER_ORDER} />;
};
